import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useInView } from '@/hooks/useInView';
import {
  ArrowRight, ArrowDown, Check,
  UserPlus, AlertTriangle, Truck, MessageCircleWarning, ClipboardList,
  FileText, Award, LayoutDashboard, Star, CheckCircle,
  Monitor, Tablet, Smartphone, Laptop, Factory, Building2,
} from 'lucide-react';
import {
  OnboardingMockup, LineIncidentsMockup, DOLMockup,
  SupplierComplaintsMockup, SupplierAuditsMockup, RfqToLoiMockup,
  CertificateMockup, SupplierDashboardsMockup, RatingsMockup,
  CorrectiveActionsMockup,
} from '@/components/mockups/VendorMockups';

const BLUE = '#38BDF8';
const GREEN = '#34D399';

/* ═══════════════════════════════════════════════════════
   APP DATA
   ═══════════════════════════════════════════════════════ */

const apps = [
  {
    id: 'supplier-onboarding', name: 'Supplier Onboarding', icon: UserPlus,
    tagline: 'Take a new supplier from first contact to fully qualified vendor. Registration, plant capability assessment, documentation collection, and multi-stage approval in one digital workflow.',
    bullets: ['Supplier self-registration and qualification portal', 'Multi-stage approval flow with plant visit, capability, and commercial sign-off', 'Onboarding checklist with readiness tracking per supplier', 'Required documentation management with gap visibility', 'Plant layout, process capability, and FMEA capture', 'Commercial terms, bank details, and compliance onboarding', 'Qualification pipeline with average cycle time tracking'],
    Mockup: OnboardingMockup,
  },
  {
    id: 'line-incidents', name: 'Line Incidents', icon: AlertTriangle,
    tagline: 'When a supplier-linked defect stops the production line, every minute counts. Capture incidents, assign containment, track severity, and close with accountability.',
    bullets: ['Supplier-linked line stoppage logging with timestamp', 'Incoming defect and mixed-part incident capture', 'Containment action tracking (sorting, quarantine, rework)', 'Severity classification: critical, major, minor', 'Incident ownership with supplier accountability', 'Closure timeline, escalation rules, and SLA enforcement', 'Incident aging visibility across open issues'],
    Mockup: LineIncidentsMockup,
  },
  {
    id: 'dol-management', name: 'DOL Management', icon: Truck,
    tagline: 'The delivery execution and deviation control layer. Track every PO commitment against actual dispatch, flag overdue suppliers, and manage shortage escalations before the line stops.',
    bullets: ['Delivery commitment vs actual tracking per PO', 'Overdue shipment alerts and deviation approvals', 'Dispatch delay logging with supplier follow-up', 'On-time delivery (OTD) monitoring and gauge', 'Shortage and emergency supply tracking', 'Supplier fulfillment dashboards with trend visibility', 'Average delay and deviation analysis'],
    Mockup: DOLMockup,
  },
  {
    id: 'supplier-complaints', name: 'Supplier Complaints', icon: MessageCircleWarning,
    tagline: 'A disciplined complaint governance system. Log supplier issues, track response SLAs, identify repeat offenders, and enforce closure accountability across the vendor base.',
    bullets: ['Supplier complaint register with category tagging', 'Issue ownership assignment and response SLA tracking', 'Closure workflow with evidence and sign-off', 'Repeat issue visibility and supplier escalation', 'Complaint Pareto by category and supplier', 'Issue aging buckets with overdue alerts', 'Supplier-side accountability and response history'],
    Mockup: SupplierComplaintsMockup,
  },
  {
    id: 'supplier-audits', name: 'Supplier Audits', icon: ClipboardList,
    tagline: 'Supplier audit governance, not just a simple audit log. Plan audits, execute checklists, score suppliers, track NC and observations, and link findings directly to corrective actions.',
    bullets: ['Audit calendar with supplier-wise scheduling', 'Checklist-based audit execution on-site or remote', 'Supplier audit scorecards with grading', 'NC and observation tracking with severity', 'Compliance gap identification and follow-up', 'Corrective actions linked directly to audit findings', 'Audit score trend analysis over quarters', 'Audit history by supplier with closure rates'],
    Mockup: SupplierAuditsMockup,
  },
  {
    id: 'rfq-to-loi', name: 'RFQ to LOI', icon: FileText,
    tagline: 'Sourcing workflow intelligence. Track the full journey from RFQ issuance to vendor shortlisting, quote comparison, commercial negotiation, and LOI approval with full decision traceability.',
    bullets: ['RFQ issuance and multi-vendor distribution', 'Quote comparison matrix with price, lead time, and quality rating', 'Commercial evaluation and negotiation stage tracking', 'Vendor shortlisting with weighted scoring', 'LOI approval progression across procurement, quality, and finance', 'Sourcing decision audit trail and approval visibility', 'Pipeline view from inquiry to commercial closure'],
    Mockup: RfqToLoiMockup,
  },
  {
    id: 'certificate-management', name: 'Certificate Management', icon: Award,
    tagline: 'Compliance readiness, not just document storage. Track IATF, ISO, NABL, and custom certificates across every vendor with expiry alerts, gap visibility, and audit-ready documentation.',
    bullets: ['Certificate repository with version history per supplier', 'Expiry alerts and auto-renewal reminders', 'Missing certificate identification and gap tracking', 'Supplier-wise compliance status (IATF, ISO, NABL, etc.)', 'Valid, expiring, and expired certificate breakdown', 'Audit-ready documentation with instant retrieval', 'Compliance record table for management review'],
    Mockup: CertificateMockup,
  },
  {
    id: 'supplier-dashboards', name: 'Supplier Dashboards', icon: LayoutDashboard,
    tagline: 'A live supplier control tower. See vendor health, open issues, pending actions, quality scores, delivery adherence, and compliance status across your entire vendor base in one view.',
    bullets: ['Supplier health summary with category-wise scoring', 'Open issues, pending documents, and active audit tracking', 'Quality, delivery, compliance, and responsiveness scores', 'Vendor health distribution (healthy, watch, at risk)', 'Pending action widgets with overdue escalation', 'Dashboard views by supplier, category, and plant', 'Performance trend visibility over time'],
    Mockup: SupplierDashboardsMockup,
  },
  {
    id: 'ratings-scorecards', name: 'Ratings & Scorecards', icon: Star,
    tagline: 'Supplier segmentation and performance governance. Rank vendors on PPM, audit scores, delivery adherence, and response time. Categorize them as preferred, conditional, under review, or blocked.',
    bullets: ['Supplier ranking board with composite scoring', 'PPM, audit score, OTD, and response time metrics', 'Complaint closure and CAPA performance tracking', 'Supplier categorization: preferred, conditional, under review, blocked', 'Performance comparison across vendors', 'Historical trend analysis and quarter-over-quarter tracking', 'Category distribution and segmentation visibility'],
    Mockup: RatingsMockup,
  },
  {
    id: 'corrective-actions', name: 'Corrective Actions', icon: CheckCircle,
    tagline: 'Supplier-side closure discipline. Track every CAPA from root cause to action plan to implementation to verification. Link actions to their source complaints, audits, or incidents.',
    bullets: ['CAPA issue log with root cause and containment tracking', 'Action owner, target date, and implementation status', 'Closure pipeline from root cause to verified closure', 'Overdue action monitoring with escalation rules', 'Linked source: complaint, audit finding, or line incident', 'Effectiveness verification and recurrence tracking', 'Aging analysis with within-target, approaching, and overdue views'],
    Mockup: CorrectiveActionsMockup,
  },
];

const packages = [
  {
    name: 'Lite',
    desc: 'For focused supplier oversight with core modules.',
    highlighted: false,
    features: { 'Supplier Records': 'Up to 50', 'Connected Apps': '3', 'Supplier Audits': false, 'Complaint Management': 'Basic', 'Supplier Dashboards': 'Basic', 'Certificate Management': false, 'Ratings & Scorecards': false, 'Corrective Actions': false, 'ERP / Third-Party Integration': false, 'Dedicated Rollout Support': false },
  },
  {
    name: 'Pro',
    desc: 'For complete vendor governance across the supply chain.',
    highlighted: true,
    features: { 'Supplier Records': 'Up to 200', 'Connected Apps': '8', 'Supplier Audits': true, 'Complaint Management': 'Advanced', 'Supplier Dashboards': 'Advanced', 'Certificate Management': true, 'Ratings & Scorecards': true, 'Corrective Actions': true, 'ERP / Third-Party Integration': true, 'Dedicated Rollout Support': true },
  },
  {
    name: 'Enterprise',
    desc: 'For multi-plant supplier networks with deep integration.',
    highlighted: false,
    features: { 'Supplier Records': 'Unlimited', 'Connected Apps': 'All', 'Supplier Audits': true, 'Complaint Management': 'Advanced + Custom', 'Supplier Dashboards': 'Advanced + Custom', 'Certificate Management': true, 'Ratings & Scorecards': true, 'Corrective Actions': true, 'ERP / Third-Party Integration': true, 'Dedicated Rollout Support': true },
  },
];

const featureRows = [
  'Supplier Records', 'Connected Apps', 'Supplier Audits', 'Complaint Management',
  'Supplier Dashboards', 'Certificate Management', 'Ratings & Scorecards',
  'Corrective Actions', 'ERP / Third-Party Integration', 'Dedicated Rollout Support',
];

/* ═══════════════════════════════════════════════════════
   SHARED COMPONENTS
   ═══════════════════════════════════════════════════════ */

function Section({ children, className = '', id }) {
  const [ref, inView] = useInView();
  return (
    <section ref={ref} id={id} className={`${inView ? 'animate-fade-in-up' : 'opacity-0'} ${className}`}>
      {children}
    </section>
  );
}

function TypewriterLine({ text, visible, startDelay = 0, highlightColor = null }) {
  return (
    <div className="overflow-hidden">
      <span className="inline-block">
        {text.split('').map((char, i) => (
          <span
            key={i}
            className={`inline-block typewriter-char ${visible ? 'typewriter-char-visible' : ''}`}
            style={{ transitionDelay: `${startDelay + i * 70}ms`, color: highlightColor || undefined }}
          >
            {char === ' ' ? '\u00A0' : char}
          </span>
        ))}
      </span>
    </div>
  );
}

function FeatureCell({ value }) {
  if (value === true) return <Check className="w-4 h-4 mx-auto" style={{ color: BLUE }} strokeWidth={2.5} />;
  if (value === false) return <span className="text-white/15">{'\u2013'}</span>;
  return <span className="text-white/70 text-sm font-medium">{value}</span>;
}

function AppDetailSection({ app, index }) {
  const isEven = index % 2 === 0;
  const Icon = app.icon;
  const MockupComponent = app.Mockup;

  return (
    <Section id={app.id} className="py-20 lg:py-28 border-t border-white/[0.04]">
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start ${!isEven ? 'lg:[direction:rtl]' : ''}`}>
          {/* Text */}
          <div className={`${!isEven ? 'lg:[direction:ltr]' : ''} pt-2`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center border" style={{ backgroundColor: `${BLUE}12`, borderColor: `${BLUE}25` }}>
                <Icon className="w-5 h-5" style={{ color: BLUE }} strokeWidth={1.7} />
              </div>
              <p className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: BLUE }}>Vendor Connect App</p>
            </div>
            <h3 className="text-2xl sm:text-3xl lg:text-4xl font-black uppercase tracking-[-0.02em] text-white leading-[1]" data-testid={`app-detail-${app.id}`}>
              {app.name}
            </h3>
            <p className="mt-4 text-[15px] text-white/45 leading-[1.7] font-medium max-w-md">
              {app.tagline}
            </p>
            <ul className="mt-6 space-y-2">
              {app.bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-3">
                  <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: BLUE }} strokeWidth={2.5} />
                  <span className="text-sm text-white/55 font-medium">{b}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Dashboard Mockup */}
          <div className={!isEven ? 'lg:[direction:ltr]' : ''}>
            <MockupComponent />
          </div>
        </div>
      </div>
    </Section>
  );
}

/* ═══════════════════════════════════════════════════════
   ANIMATED SUPPLIER NETWORK
   ═══════════════════════════════════════════════════════ */

function SupplierNetworkAnimation() {
  const [ref, inView] = useInView();
  const [pulse, setPulse] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const t = setInterval(() => setPulse(p => (p + 1) % 6), 800);
    return () => clearInterval(t);
  }, [inView, setPulse]);

  const ancillaries = [
    { label: 'Tier 2 Forge', x: 60, y: 50, angle: -60 },
    { label: 'Castings Vendor', x: 140, y: 20, angle: -30 },
    { label: 'Sheet Metal', x: 260, y: 20, angle: 30 },
    { label: 'Rubber Parts', x: 340, y: 50, angle: 60 },
    { label: 'Electronics', x: 60, y: 190, angle: -120 },
    { label: 'Heat Treatment', x: 340, y: 190, angle: 120 },
  ];

  return (
    <section ref={ref} className={`py-20 lg:py-28 bg-[#0A0A0A] border-t border-white/[0.04] overflow-hidden ${inView ? 'animate-fade-in-up' : 'opacity-0'}`} data-testid="supplier-network-animation">
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
        <div className="text-center mb-12">
          <p className="text-sm font-bold uppercase tracking-[0.25em] mb-6" style={{ color: BLUE }}>Connected Ecosystem</p>
          <h2 className="text-3xl sm:text-4xl font-black uppercase tracking-[-0.02em] text-white leading-[1.05]">
            Your Factory. Your Vendors.<br />One Digital Network.
          </h2>
          <p className="mt-5 text-base text-white/40 max-w-xl mx-auto font-medium leading-[1.7]">
            Vendor Connect brings every ancillary supplier into a shared digital layer with the main plant.
          </p>
        </div>

        {/* Network Visualization */}
        <div className="max-w-2xl mx-auto">
          <svg viewBox="0 0 400 240" className="w-full" style={{ maxHeight: '360px' }}>
            {/* Connection lines from ancillaries to center */}
            {ancillaries.map((a, i) => {
              const active = pulse === i || pulse === (i + 3) % 6;
              return (
                <line key={`line-${i}`} x1={a.x} y1={a.y} x2={200} y2={120}
                  stroke={BLUE}
                  strokeWidth={active ? 2 : 1}
                  opacity={active ? 0.5 : 0.1}
                  strokeDasharray={active ? 'none' : '4,4'}
                  style={{ transition: 'all 0.6s ease' }}
                />
              );
            })}

            {/* Data pulse dots traveling on lines */}
            {ancillaries.map((a, i) => {
              const active = pulse === i || pulse === (i + 3) % 6;
              if (!active) return null;
              const midX = (a.x + 200) / 2;
              const midY = (a.y + 120) / 2;
              return (
                <circle key={`pulse-${i}`} cx={midX} cy={midY} r="3" fill={BLUE} opacity="0.7">
                  <animate attributeName="cx" from={a.x} to={200} dur="0.8s" repeatCount="1" />
                  <animate attributeName="cy" from={a.y} to={120} dur="0.8s" repeatCount="1" />
                </circle>
              );
            })}

            {/* Central Factory (OEM / Tier 1) */}
            <rect x="160" y="90" width="80" height="60" rx="12" fill={`${BLUE}15`} stroke={BLUE} strokeWidth="2" opacity="0.8" />
            <Factory x="185" y="105" width="30" height="30" stroke={BLUE} strokeWidth="1.5" fill="none" opacity="0.7" />
            <text x="200" y="165" textAnchor="middle" fill="white" fontSize="9" fontWeight="700" opacity="0.7">YOUR PLANT</text>
            <text x="200" y="175" textAnchor="middle" fill={BLUE} fontSize="7" fontWeight="600" opacity="0.5">OEM / Tier 1</text>

            {/* Ancillary Factories */}
            {ancillaries.map((a, i) => {
              const active = pulse === i || pulse === (i + 3) % 6;
              return (
                <g key={`factory-${i}`}>
                  <rect x={a.x - 28} y={a.y - 18} width="56" height="36" rx="8"
                    fill={active ? `${BLUE}18` : 'rgba(255,255,255,0.03)'}
                    stroke={active ? BLUE : 'rgba(255,255,255,0.1)'}
                    strokeWidth={active ? 1.5 : 0.8}
                    style={{ transition: 'all 0.5s ease' }}
                  />
                  <Building2 x={a.x - 8} y={a.y - 12} width="16" height="16"
                    stroke={active ? BLUE : 'rgba(255,255,255,0.3)'}
                    strokeWidth="1.2" fill="none"
                    style={{ transition: 'all 0.5s ease' }}
                  />
                  <text x={a.x} y={a.y + 10} textAnchor="middle" fill={active ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.25)'} fontSize="6" fontWeight="600"
                    style={{ transition: 'all 0.5s ease' }}>
                    {a.label}
                  </text>
                </g>
              );
            })}
          </svg>
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════
   DIFFERENTIATOR SECTION
   ═══════════════════════════════════════════════════════ */

function DifferentiatorSection() {
  const [ref, inView] = useInView();
  const pillars = [
    { label: 'Onboarding', desc: 'Register, qualify, and approve vendors digitally' },
    { label: 'Quality', desc: 'Complaints, audits, and corrective actions in one view' },
    { label: 'Compliance', desc: 'Certificates, expiry tracking, and renewal management' },
    { label: 'Delivery', desc: 'DOL tracking, deviations, and fulfillment visibility' },
    { label: 'Performance', desc: 'Ratings, scorecards, and supplier ranking' },
    { label: 'Governance', desc: 'Dashboards, health scores, and executive reporting' },
  ];

  return (
    <section ref={ref} className={`py-28 lg:py-40 bg-[#060606] border-t border-white/[0.04] overflow-hidden ${inView ? 'animate-fade-in-up' : 'opacity-0'}`} data-testid="vendor-differentiator">
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: BLUE }} />
              <p className="text-xs font-bold uppercase tracking-[0.25em]" style={{ color: BLUE }}>Product Differentiator</p>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-[-0.02em] text-white leading-[1]">
              Extend Your Factory<br />Beyond the Plant Gate
            </h2>
            <p className="mt-6 text-base text-white/40 leading-[1.7] font-medium max-w-lg">
              The factory does not end at the gate. Supplier performance, audits, complaints, certificates, and delivery discipline directly affect plant execution. Vendor Connect makes that entire ecosystem visible and manageable.
            </p>
            <ul className="mt-8 space-y-3">
              {[
                'One connected digital layer across your entire vendor base',
                'Supplier quality, delivery, and compliance in a single view',
                'Linked workflows from complaints to CAPA to closure',
                'Audit-ready documentation and certificate management',
                'Real-time supplier health scores and performance rankings',
              ].map((b, i) => (
                <li key={i} className="flex items-start gap-3">
                  <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: BLUE }} strokeWidth={2.5} />
                  <span className="text-sm text-white/55 font-medium">{b}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Pillars Grid */}
          <div className="grid grid-cols-2 gap-3">
            {pillars.map((p, i) => (
              <div key={i} className="p-5 rounded-xl bg-white/[0.03] border border-white/[0.06] hover:border-[#38BDF8]/20 transition-all duration-300">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center mb-3" style={{ backgroundColor: `${BLUE}12`, border: `1px solid ${BLUE}20` }}>
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: BLUE, opacity: 0.6 }} />
                </div>
                <p className="text-sm font-bold text-white/80">{p.label}</p>
                <p className="mt-1 text-[12px] text-white/30 leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════
   CROSS-PLATFORM SECTION
   ═══════════════════════════════════════════════════════ */

function CrossPlatformSection() {
  const [ref, inView] = useInView();
  return (
    <section ref={ref} className={`py-24 lg:py-36 bg-[#060606] border-t border-white/[0.04] overflow-hidden ${inView ? 'animate-fade-in-up' : 'opacity-0'}`} data-testid="vc-cross-platform">
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
        <div className="text-center mb-16">
          <p className="text-sm font-bold uppercase tracking-[0.25em] mb-6" style={{ color: BLUE }}>Built for Every Screen</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-[-0.02em] text-white leading-[1]">
            One Supplier Platform.<br />Every Device.
          </h2>
          <p className="mt-5 text-base text-white/40 max-w-xl mx-auto font-medium leading-[1.7]">
            Procurement desk, quality office, supplier site, or management review. Vendor Connect works across desktop, laptop, tablet, and mobile.
          </p>
        </div>

        {/* Device Mockups */}
        <div className="relative flex items-end justify-center gap-4 sm:gap-6 lg:gap-8 min-h-[300px] sm:min-h-[350px] lg:min-h-[400px]">
          {/* Desktop Monitor */}
          <div className="hidden lg:block relative" style={{ width: '420px' }}>
            <div className="rounded-xl border-2 border-white/[0.1] bg-[#0A0A0A] overflow-hidden">
              <div className="flex items-center gap-1.5 px-3 py-2 bg-white/[0.03] border-b border-white/[0.06]">
                <div className="flex gap-1"><div className="w-2 h-2 rounded-full bg-white/10" /><div className="w-2 h-2 rounded-full bg-white/10" /><div className="w-2 h-2 rounded-full bg-white/10" /></div>
                <span className="text-[9px] text-white/20 font-mono ml-2">app.mushinlabs.com/vendors</span>
              </div>
              <div className="p-3">
                <div className="grid grid-cols-4 gap-2 mb-3">
                  {[{l:'Suppliers',v:'184'},{l:'Open Issues',v:'23'},{l:'Avg Rating',v:'78%'},{l:'Audits Due',v:'6'}].map(k => (
                    <div key={k.l} className="p-2 rounded-md bg-white/[0.03] border border-white/[0.04]">
                      <p className="text-[7px] text-white/20 uppercase">{k.l}</p>
                      <p className="text-[11px] font-black text-white/60">{k.v}</p>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2 mb-2">
                  {[82,87,71,65].map((v,i) => (
                    <div key={i} className="flex-1 h-3 bg-white/[0.03] rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{width:`${v}%`, backgroundColor: v >= 80 ? `${GREEN}50` : v >= 70 ? `${BLUE}40` : '#FBBF2450'}} />
                    </div>
                  ))}
                </div>
                {[1,2,3].map(i => <div key={i} className="h-3.5 mb-1 rounded bg-white/[0.02] border border-white/[0.03]" />)}
              </div>
            </div>
            <div className="mx-auto mt-0 flex flex-col items-center">
              <div className="w-16 h-6 bg-white/[0.04] border-x-2 border-white/[0.06]" />
              <div className="w-28 h-2 bg-white/[0.04] rounded-b-lg border border-t-0 border-white/[0.06]" />
            </div>
          </div>

          {/* Laptop */}
          <div className="relative" style={{ width: 'min(320px, 45vw)' }}>
            <div className="rounded-t-lg border-2 border-b-0 border-white/[0.1] bg-[#0A0A0A] overflow-hidden">
              <div className="flex items-center gap-1 px-2 py-1.5 bg-white/[0.03] border-b border-white/[0.06]">
                <div className="flex gap-1"><div className="w-1.5 h-1.5 rounded-full bg-white/10" /><div className="w-1.5 h-1.5 rounded-full bg-white/10" /><div className="w-1.5 h-1.5 rounded-full bg-white/10" /></div>
              </div>
              <div className="p-2">
                <div className="grid grid-cols-3 gap-1.5 mb-2">
                  {[{l:'Active',v:'184'},{l:'Issues',v:'23'},{l:'Rating',v:'78%'}].map(k => (
                    <div key={k.l} className="p-1.5 rounded bg-white/[0.03] border border-white/[0.04]">
                      <p className="text-[6px] text-white/20 uppercase">{k.l}</p>
                      <p className="text-[9px] font-black text-white/60">{k.v}</p>
                    </div>
                  ))}
                </div>
                {[1,2,3].map(i => (
                  <div key={i} className="flex items-center gap-1.5 mb-1">
                    <div className="w-1.5 h-1.5 rounded-full" style={{backgroundColor: i === 3 ? '#FBBF24' : BLUE, opacity: 0.5}} />
                    <div className="flex-1 h-2.5 rounded bg-white/[0.03]" />
                    <div className="w-8 h-2.5 rounded" style={{backgroundColor: i === 1 ? `${GREEN}15` : i === 2 ? `${BLUE}15` : '#FBBF2415'}} />
                  </div>
                ))}
                <div className="mt-1.5 h-8 rounded bg-white/[0.02] border border-white/[0.03]" />
              </div>
            </div>
            <div className="w-[115%] -ml-[7.5%] h-3 bg-white/[0.04] rounded-b-xl border border-t-2 border-white/[0.08]" />
          </div>

          {/* Tablet */}
          <div className="relative hidden sm:block" style={{ width: 'min(180px, 25vw)' }}>
            <div className="rounded-xl border-2 border-white/[0.1] bg-[#0A0A0A] overflow-hidden p-1">
              <div className="p-2">
                <p className="text-[5px] text-white/15 uppercase tracking-wider mb-1.5">Audit Scorecard</p>
                <div className="flex gap-2 mb-2 justify-center">
                  {[{s:88,c:GREEN},{s:74,c:'#FBBF24'},{s:91,c:GREEN}].map((a,i) => (
                    <svg key={i} viewBox="0 0 30 30" className="w-7 h-7">
                      <circle cx="15" cy="15" r="11" fill="none" stroke="rgba(255,255,255,0.04)" strokeWidth="2.5" />
                      <circle cx="15" cy="15" r="11" fill="none" stroke={a.c} strokeWidth="2.5" opacity="0.5"
                        strokeDasharray={`${(a.s/100)*69.1} 69.1`} strokeLinecap="round" transform="rotate(-90 15 15)" />
                      <text x="15" y="17" textAnchor="middle" fill="white" fontSize="6" fontWeight="900" opacity="0.6">{a.s}</text>
                    </svg>
                  ))}
                </div>
                {[1,2,3].map(i => (
                  <div key={i} className="flex items-center gap-1 mb-1">
                    <div className="flex-1 h-2.5 rounded bg-white/[0.03]" />
                    <div className="w-5 h-2.5 rounded" style={{backgroundColor: `${BLUE}15`}} />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Phone */}
          <div className="relative" style={{ width: 'min(100px, 18vw)' }}>
            <div className="rounded-2xl border-2 border-white/[0.1] bg-[#0A0A0A] overflow-hidden p-1">
              <div className="mx-auto w-10 h-1.5 bg-white/[0.06] rounded-full mb-1" />
              <div className="p-1.5">
                <div className="p-1.5 rounded bg-white/[0.03] border border-white/[0.04] mb-1.5">
                  <p className="text-[5px] text-white/20 uppercase">CAPA Alert</p>
                  <p className="text-[8px] font-black" style={{color: '#FBBF24'}}>3 Overdue</p>
                </div>
                {[1,2,3].map(i => (
                  <div key={i} className="flex items-center gap-1 mb-1">
                    <div className="w-1 h-1 rounded-full" style={{backgroundColor: i === 1 ? '#E02D45' : BLUE, opacity: 0.5}} />
                    <div className="flex-1 h-2 rounded bg-white/[0.03]" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-center gap-8 sm:gap-12 mt-10">
          {[
            { icon: Monitor, label: 'Desktop', hide: 'hidden lg:flex' },
            { icon: Laptop, label: 'Laptop', hide: 'flex' },
            { icon: Tablet, label: 'Tablet', hide: 'hidden sm:flex' },
            { icon: Smartphone, label: 'Mobile', hide: 'flex' },
          ].map(d => (
            <div key={d.label} className={`${d.hide} items-center gap-2`}>
              <d.icon className="w-4 h-4 text-white/25" strokeWidth={1.5} />
              <span className="text-xs text-white/30 font-medium">{d.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════
   PAGE
   ═══════════════════════════════════════════════════════ */

export default function MushinConnectPage() {
  const [heroVisible, setHeroVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setHeroVisible(true), 500);
    return () => clearTimeout(t);
  }, [setHeroVisible]);

  const scrollToApp = useCallback((id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  return (
    <div>
      {/* 1. HERO */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 bg-[#060606] overflow-hidden" data-testid="vendor-connect-hero">
        <div className="absolute inset-0 opacity-[0.04]" style={{
          backgroundImage: `linear-gradient(${BLUE}30 1px, transparent 1px), linear-gradient(90deg, ${BLUE}30 1px, transparent 1px)`,
          backgroundSize: '80px 80px'
        }} />
        <div className="absolute top-0 right-0 w-[600px] h-[400px] rounded-full blur-[200px]" style={{ backgroundColor: `${BLUE}06` }} />

        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 relative z-10">
          <div className={`transition-all duration-[1200ms] ease-out ${heroVisible ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: '100ms' }}>
            <div className="flex items-center gap-3 mb-8">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: BLUE }} />
              <p className="text-xs font-bold uppercase tracking-[0.25em]" style={{ color: BLUE }}>Mushin Vendor Connect</p>
            </div>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-[4.5rem] font-black uppercase tracking-[-0.03em] text-white leading-[0.92] max-w-4xl" data-testid="vendor-connect-heading">
            <TypewriterLine text="The Operating Layer for" visible={heroVisible} startDelay={400} />
            <TypewriterLine text="Vendor Governance" visible={heroVisible} startDelay={2000} highlightColor={BLUE} />
          </h1>

          <div className={`transition-all duration-[1400ms] ease-out ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '3600ms' }}>
            <div className="mt-10 h-[3px] w-16 hero-accent" style={{ backgroundColor: BLUE, animationDelay: '3700ms' }} />
            <p className="mt-8 text-lg text-white/50 max-w-2xl leading-[1.7] font-medium">
              Connect your factory with its vendors in one digital system. Onboarding, audits, scorecards, complaints, certificates, and supplier performance in a single platform.
            </p>
          </div>

          <div className={`mt-10 flex flex-wrap gap-4 transition-all duration-[1400ms] ease-out ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '4000ms' }}>
            <Link to="/contact" className="text-white px-8 py-3.5 rounded-full font-bold text-sm inline-flex items-center gap-2 transition-all duration-300 hover:shadow-xl" style={{ backgroundColor: BLUE }} data-testid="vendor-connect-demo-cta">
              Book a Demo <ArrowRight className="w-4 h-4" />
            </Link>
            <a href="#vendor-apps" onClick={(e) => { e.preventDefault(); document.getElementById('vendor-apps')?.scrollIntoView({ behavior: 'smooth' }); }} className="border-2 border-white/15 hover:border-white/30 text-white/50 hover:text-white px-8 py-3.5 rounded-full font-bold text-sm transition-all duration-300">
              Explore Vendor Apps
            </a>
          </div>
        </div>

        <div className={`absolute bottom-6 left-1/2 -translate-x-1/2 transition-all duration-1000 ${heroVisible ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: '4600ms' }}>
          <ArrowDown className="w-5 h-5 text-white/15 animate-bounce" />
        </div>
      </section>

      {/* 2. ANIMATED SUPPLIER NETWORK */}
      <SupplierNetworkAnimation />

      {/* 3. APP GRID */}
      <Section className="py-20 lg:py-28 bg-[#060606] border-t border-white/[0.04]" id="vendor-apps">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="max-w-3xl mb-14">
            <p className="text-sm font-bold uppercase tracking-[0.25em] mb-6" style={{ color: BLUE }}>Vendor Apps</p>
            <h2 className="text-3xl sm:text-4xl font-black uppercase tracking-[-0.02em] text-white leading-[1.05]">
              One Connected System.<br />Vendor Apps for Every Function.
            </h2>
            <p className="mt-5 text-base text-white/40 max-w-xl leading-[1.7] font-medium">
              Vendor Connect is made up of modular apps that digitize the entire supplier ecosystem. Click any app to explore its full scope.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {apps.map((app) => {
              const Icon = app.icon;
              return (
                <button
                  key={app.id}
                  onClick={() => scrollToApp(app.id)}
                  className="group text-left bg-white/[0.03] border border-white/[0.06] rounded-xl p-4 hover:bg-white/[0.06] hover:border-[#38BDF8]/25 transition-all duration-300 hover:-translate-y-0.5 cursor-pointer"
                  data-testid={`app-nav-${app.id}`}
                >
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-3 border" style={{ backgroundColor: `${BLUE}10`, borderColor: `${BLUE}20` }}>
                    <Icon className="w-[18px] h-[18px]" style={{ color: BLUE }} strokeWidth={1.7} />
                  </div>
                  <p className="text-[13px] font-bold text-white/80 leading-snug">{app.name}</p>
                  <p className="mt-1 text-[11px] text-white/25 font-medium group-hover:text-white/40 transition-colors">View details</p>
                </button>
              );
            })}
          </div>
        </div>
      </Section>

      {/* 4. DETAILED APP SECTIONS */}
      <div className="bg-[#060606]">
        {apps.map((app, i) => (
          <AppDetailSection key={app.id} app={app} index={i} />
        ))}
      </div>

      {/* 5. DIFFERENTIATOR */}
      <DifferentiatorSection />

      {/* 6. CROSS-PLATFORM */}
      <CrossPlatformSection />

      {/* 7. PACKAGES */}
      <Section className="py-24 lg:py-36 bg-[#0A0A0A] border-t border-white/[0.04]" id="vc-packages">
        <div className="max-w-[1200px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="text-center mb-16">
            <p className="text-sm font-bold uppercase tracking-[0.25em] mb-6" style={{ color: BLUE }}>Packages</p>
            <h2 className="text-3xl sm:text-4xl font-black uppercase tracking-[-0.02em] text-white leading-[1.05]">
              Choose Your Vendor Connect Package
            </h2>
            <p className="mt-4 text-base text-white/40 max-w-lg mx-auto font-medium">
              Start with core vendor oversight. Scale as your supplier network grows.
            </p>
          </div>

          {/* Mobile */}
          <div className="lg:hidden space-y-6">
            {packages.map((pkg, i) => (
              <div key={i} className={`rounded-2xl p-6 border ${pkg.highlighted ? 'border-[#38BDF8]/30 bg-[#38BDF8]/[0.04]' : 'border-white/[0.06] bg-white/[0.02]'}`}>
                {pkg.highlighted && <p className="text-[10px] font-bold uppercase tracking-[0.2em] mb-3" style={{ color: BLUE }}>Most Popular</p>}
                <h3 className="text-2xl font-black uppercase text-white">{pkg.name}</h3>
                <p className="mt-2 text-sm text-white/40 font-medium">{pkg.desc}</p>
                <div className="mt-6 space-y-3">
                  {featureRows.map((feat, j) => (
                    <div key={j} className="flex justify-between items-center py-1.5 border-b border-white/[0.04]">
                      <span className="text-xs text-white/40 font-medium">{feat}</span>
                      <FeatureCell value={pkg.features[feat]} />
                    </div>
                  ))}
                </div>
                <div className="mt-6">
                  <Link to="/contact" className={`w-full flex justify-center py-3 rounded-full font-bold text-sm transition-colors ${pkg.highlighted ? 'text-white' : 'text-white/70 border border-white/15 hover:border-white/30'}`} style={pkg.highlighted ? { backgroundColor: BLUE } : {}}>
                    Talk to Sales
                  </Link>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop */}
          <div className="hidden lg:block">
            <div className="rounded-2xl border border-white/[0.06] overflow-hidden">
              <div className="grid grid-cols-4 border-b border-white/[0.06]">
                <div className="p-6 bg-white/[0.02]" />
                {packages.map((pkg, i) => (
                  <div key={i} className={`p-6 text-center ${pkg.highlighted ? 'bg-[#38BDF8]/[0.04] border-x border-[#38BDF8]/15' : 'bg-white/[0.02]'}`}>
                    {pkg.highlighted && <p className="text-[10px] font-bold uppercase tracking-[0.2em] mb-2" style={{ color: BLUE }}>Most Popular</p>}
                    <h3 className="text-xl font-black uppercase text-white">{pkg.name}</h3>
                    <p className="mt-1.5 text-xs text-white/35 font-medium">{pkg.desc}</p>
                  </div>
                ))}
              </div>
              {featureRows.map((feat, j) => (
                <div key={j} className="grid grid-cols-4 border-b border-white/[0.04] last:border-b-0">
                  <div className="p-4 pl-6 flex items-center">
                    <span className="text-sm text-white/50 font-medium">{feat}</span>
                  </div>
                  {packages.map((pkg, i) => (
                    <div key={i} className={`p-4 flex items-center justify-center ${pkg.highlighted ? 'bg-[#38BDF8]/[0.02] border-x border-[#38BDF8]/8' : ''}`}>
                      <FeatureCell value={pkg.features[feat]} />
                    </div>
                  ))}
                </div>
              ))}
              <div className="grid grid-cols-4 border-t border-white/[0.06]">
                <div className="p-6" />
                {packages.map((pkg, i) => (
                  <div key={i} className={`p-6 flex justify-center ${pkg.highlighted ? 'bg-[#38BDF8]/[0.04] border-x border-[#38BDF8]/15' : ''}`}>
                    <Link to="/contact" className={`px-8 py-2.5 rounded-full font-bold text-sm transition-all duration-300 ${pkg.highlighted ? 'text-white hover:shadow-lg' : 'text-white/60 border border-white/15 hover:border-white/30 hover:text-white'}`} style={pkg.highlighted ? { backgroundColor: BLUE } : {}}>
                      Talk to Sales
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* 8. FINAL CTA */}
      <section className="py-28 lg:py-40 relative overflow-hidden bg-[#060606] border-t border-white/[0.04]" data-testid="vendor-connect-cta">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full blur-[150px]" style={{ backgroundColor: `${BLUE}06` }} />
        <div className="max-w-4xl mx-auto px-6 sm:px-10 lg:px-16 text-center relative z-10">
          <p className="text-sm font-bold uppercase tracking-[0.25em] mb-8" style={{ color: BLUE }}>Get Started</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-[-0.02em] text-white leading-[1]">
            Connect Your Supplier<br />Ecosystem with Mushin
          </h2>
          <p className="mt-6 text-white/35 text-lg max-w-lg mx-auto font-medium leading-[1.7]">
            Bring vendor governance, quality, audits, and performance into one digital backbone. Start with the apps you need.
          </p>
          <div className="mt-10 flex flex-wrap gap-4 justify-center">
            <Link to="/contact" className="text-white px-10 py-4 rounded-full font-bold text-base inline-flex items-center gap-2.5 transition-all duration-300 hover:shadow-xl" style={{ backgroundColor: BLUE }} data-testid="vendor-connect-final-cta">
              Book a Demo <ArrowRight className="w-5 h-5" />
            </Link>
            <a href="mailto:sales@mushinlabs.com" className="border-2 border-white/15 hover:border-white/30 text-white/45 hover:text-white px-10 py-4 rounded-full font-bold text-base transition-all duration-300">
              Talk to Sales
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
